<template>
    <div v-if="shouldShowAd && randomAd && randomAd.active" class="container-fluid">
        <div class="ad-container border-radius-xl mt-4 p-3">
            <a :href="randomAd.url" target="_blank" rel="noopener noreferrer" class="ad-link">
                <img 
                    v-if="randomAd.image_src" 
                    :src="randomAd.image_src" 
                    :alt="randomAd.image_alt || randomAd.name" 
                    :style="randomAd.image_style || 'max-width: 100%; height: auto;'"
                    class="ad-image"
                />
                <div v-if="randomAd.content" class="ad-content mt-2">
                    <p class="mb-0">{{ randomAd.content }}</p>
                </div>
            </a>
            <div class="ad-label">
                <small class="text-muted">Sponsored</small>
            </div>
        </div>
    </div>
    <!-- Loading state -->
    <div v-else-if="isLoading" class="container-fluid">
        <div class="ad-container border-radius-xl mt-4 p-3 skeleton-animate" style="min-height: 200px;">
        </div>
    </div>
</template>

<script setup>
import { computed, onMounted, watch, ref } from 'vue';
import { useStore } from 'vuex';

const store = useStore();
const hasFetchedAd = ref(false);

// Computed properties
const randomAd = computed(() => store.getters.stRandomAd);
const isLoading = computed(() => store.getters.stAdsLoading);
const stSessionData = computed(() => store.getters.stSessionData);
const stLocationDetail = computed(() => store.getters.stLocationDetail);

// Determine if ads should be shown:
// - Show for users who don't have a location yet
// - Show for users in locations that have ads enabled
const shouldShowAd = computed(() => {
    // If no session data, show ads anyway (for new/unauthenticated users on Setup page)
    if (!stSessionData.value || Object.keys(stSessionData.value).length === 0) {
        return true;
    }
    
    // If user has no location, show ads (new users)
    const hasLocation = stSessionData.value?.locationid;
    if (!hasLocation) {
        return true;
    }
    
    // If location detail is available, check if ads are enabled
    if (stLocationDetail.value) {
        // Check for ads_enabled flag (assuming this field exists in location detail)
        return stLocationDetail.value.show_ads === true || 
               stLocationDetail.value.ads_enabled === true ||
               stLocationDetail.value.showads === 1 ||
               stLocationDetail.value.showads === '1';
    }
    
    // Default: don't show ads if location exists but we can't determine ads setting
    return false;
});

// Function to fetch ad
const fetchAd = async () => {
    if (hasFetchedAd.value) return;
    
    try {
        hasFetchedAd.value = true;
        await store.dispatch('fetchRandomAd', 'page');
    } catch (error) {
        console.error('Failed to fetch ad:', error);
        hasFetchedAd.value = false; // Allow retry on error
    }
};

// Fetch ad on component mount
onMounted(() => {
    if (shouldShowAd.value) {
        fetchAd();
    }
});

// Watch for shouldShowAd to become true (e.g., when session/location data loads)
watch(shouldShowAd, (newValue) => {
    if (newValue && !hasFetchedAd.value) {
        fetchAd();
    }
});
</script>

<style scoped>
.ad-container {
    background-color: #f8f9fa;
    border: 1px solid #e9ecef;
    text-align: center;
}

.ad-link {
    text-decoration: none;
    color: inherit;
    display: block;
}

.ad-link:hover {
    opacity: 0.9;
}

.ad-image {
    border-radius: 8px;
}

.ad-content {
    color: #495057;
    font-size: 0.9rem;
}

.ad-label {
    text-align: right;
    margin-top: 8px;
}

.skeleton-animate {
    animation: skeleton-loading 1.5s infinite;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
}

@keyframes skeleton-loading {
    0% {
        background-position: 200% 0;
    }
    100% {
        background-position: -200% 0;
    }
}
</style>
